import React from 'react';
import styled from 'styled-components';
import { TrendingUp, DollarSign, Activity, CheckCircle } from 'lucide-react';
import { useBraipStats } from '../../hooks/useBraip';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { motion } from 'framer-motion';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing[4]};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[5]};
  transition: all ${props => props.theme.animations.duration.normal};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StatIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: ${props => props.$color}20;
  color: ${props => props.$color};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const StatChange = styled.div<{ $positive?: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.$positive ? props.theme.colors.success : props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

interface BraipStatsProps {
  userId?: string;
}

const BraipStats: React.FC<BraipStatsProps> = ({ userId: propUserId }) => {
  const { user } = useCurrentUser();
  const userId = propUserId || user?.uid;
  const { data: stats, isLoading } = useBraipStats(userId);

  if (isLoading || !stats) {
    return (
      <StatsGrid>
        {[1, 2, 3, 4].map(i => (
          <StatCard key={i}>
            <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity className="animate-pulse" size={24} />
            </div>
          </StatCard>
        ))}
      </StatsGrid>
    );
  }

  return (
    <StatsGrid>
      <StatCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0 }}
      >
        <StatHeader>
          <StatIcon $color="#10b981">
            <TrendingUp size={20} />
          </StatIcon>
          <StatLabel>Vendas Hoje</StatLabel>
        </StatHeader>
        <StatValue>{stats.totalVendasHoje}</StatValue>
        <StatChange $positive={stats.totalVendasHoje > 0}>
          Via webhook Braip
        </StatChange>
      </StatCard>

      <StatCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <StatHeader>
          <StatIcon $color="#3b82f6">
            <DollarSign size={20} />
          </StatIcon>
          <StatLabel>Vendas no Mês</StatLabel>
        </StatHeader>
        <StatValue>{stats.totalVendasMes}</StatValue>
        <StatChange>
          Total acumulado
        </StatChange>
      </StatCard>

      <StatCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <StatHeader>
          <StatIcon $color="#f59e0b">
            <Activity size={20} />
          </StatIcon>
          <StatLabel>Total Webhooks</StatLabel>
        </StatHeader>
        <StatValue>{stats.totalWebhooks}</StatValue>
        <StatChange>
          Recebidos
        </StatChange>
      </StatCard>

      <StatCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <StatHeader>
          <StatIcon $color={stats.integracaoAtiva ? '#10b981' : '#ef4444'}>
            <CheckCircle size={20} />
          </StatIcon>
          <StatLabel>Status</StatLabel>
        </StatHeader>
        <StatValue style={{ fontSize: '1.5rem' }}>
          {stats.integracaoAtiva ? 'Ativa' : 'Inativa'}
        </StatValue>
        <StatChange $positive={stats.integracaoAtiva}>
          {stats.integracaoAtiva ? 'Integração funcionando' : 'Configure a integração'}
        </StatChange>
      </StatCard>
    </StatsGrid>
  );
};

export default BraipStats;
